/**
 * Milestone Offer Wall — partner roster (v1).
 *
 * Logos are bundled locally from src/assets/partners for pixel-perfect, offline-safe rendering.
 * A wordmark fallback in `color_hex` renders if the image fails to load.
 *
 * Keep copy generic until partner deals are signed — no dollar amounts.
 */

import adtLogo from "@/assets/partners/adt.png";
import ahsLogo from "@/assets/partners/american-home-shield.png";
import renewalLogo from "@/assets/partners/renewal-by-andersen.png";
import leaffilterLogo from "@/assets/partners/leaffilter.png";

export type JourneyTrack =
  | "homebuyer"
  | "homeowner"
  | "seller"
  | "investor"
  | "renter"
  | "relocator";

export type Partner = {
  slug: string;
  brandName: string;
  domain: string; // kept for reference / outbound links
  logo: string; // bundled logo asset
  colorHex: string; // brand color for fallback wordmark + accents
  tagline: string; // short value prop
  milestoneUnlock: string; // when the offer kicks in
  tracks: JourneyTrack[];
  category: string;
};

/**
 * Single source of truth for both the /offers wall and the teaser cards.
 */
export const offerPartners: Partner[] = [
  {
    slug: "adt",
    brandName: "ADT",
    domain: "adt.com",
    logo: adtLogo,
    colorHex: "#0046AD",
    tagline: "24/7 smart home security & monitoring",
    milestoneUnlock: "After closing on your home",
    tracks: ["homebuyer", "homeowner", "relocator"],
    category: "Security",
  },
  {
    slug: "american-home-shield",
    brandName: "American Home Shield",
    domain: "ahs.com",
    logo: ahsLogo,
    colorHex: "#00558C",
    tagline: "Home warranty for major systems & appliances",
    milestoneUnlock: "After closing on your home",
    tracks: ["homebuyer", "homeowner", "seller", "relocator"],
    category: "Home Warranty",
  },
  {
    slug: "renewal-by-andersen",
    brandName: "Renewal by Andersen",
    domain: "renewalbyandersen.com",
    logo: renewalLogo,
    colorHex: "#003B71",
    tagline: "Premium replacement windows & patio doors",
    milestoneUnlock: "After your first homeowner milestone",
    tracks: ["homeowner", "investor"],
    category: "Home Improvement",
  },
  {
    slug: "leaffilter",
    brandName: "LeafFilter",
    domain: "leaffilter.com",
    logo: leaffilterLogo,
    colorHex: "#1F8A3B",
    tagline: "Gutter protection that keeps debris out for good",
    milestoneUnlock: "After your first homeowner milestone",
    tracks: ["homeowner", "investor"],
    category: "Home Improvement",
  },
];

/** Identical teaser copy across every card — keeps us honest until partners sign. */
export const GENERIC_CASHBACK_TEASER =
  "Earn Approach Points + exclusive cashback at closing";

export const FUNDING_DISCLOSURE =
  "All cashback and partner credits are funded directly by Home Approach — out of our own pocket. No partner pays for placement.";

export const trackLabel: Record<JourneyTrack, string> = {
  homebuyer: "Homebuyer",
  homeowner: "Homeowner",
  seller: "Seller",
  investor: "Investor",
  renter: "Renter",
  relocator: "Relocator",
};

/** Returns the bundled logo asset for a partner. */
export function logoUrlFor(partner: Partner): string {
  return partner.logo;
}